import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';

/**
 * Snug Whiskey Fest — Attendee Modal
 *
 * Shown on the cart page when a user clicks + on a ticket line item.
 * Collects one new attendee's details and POSTs to /cart/add.js to create
 * a new, distinct line item for the same variant. Re-renders the cart
 * section via CartUpdateEvent.
 */
class SwfAttendeeModal extends Component {
  #lastFocus = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('swf:open-attendee-modal', this.#onOpen);
    document.addEventListener('keydown', this.#onKeydown);
    this.refs.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('swf:open-attendee-modal', this.#onOpen);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onSubmit = (event) => this.submit(event);

  #onOpen = (event) => {
    const { variantId, productTitle } = event.detail ?? {};
    if (!variantId) return;

    this.#lastFocus = document.activeElement;
    this.refs.variantId.value = variantId;

    if (productTitle && this.refs.subtitle) {
      this.refs.subtitle.textContent = `Enter the attendee's details for ${productTitle}.`;
    }

    this.#clearForm();
    this.hidden = false;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.refs.nameInput?.focus();
    });
  };

  #onKeydown = (event) => {
    if (this.hidden) return;
    if (event.key === 'Escape') this.close();
  };

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    this.#clearForm();
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  }

  async submit(event) {
    event.preventDefault();
    const form = this.refs.form;
    const submitBtn = this.refs.submitBtn;
    const errorEl = this.refs.error;

    if (!form.reportValidity()) return;

    // Unique id so Shopify never merges this into an existing cart line
    this.refs.lineId.value = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const cartItemsEls = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsEls)
      .map((el) => el.dataset?.sectionId)
      .filter(Boolean);

    const formData = new FormData(form);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding…';
    errorEl.hidden = true;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add ticket.');
      }

      // Fetch updated cart for the CartUpdateEvent payload
      const cartRes = await fetch(`${root}cart.js`);
      const cart = await cartRes.json();

      const sections = data.sections ? data.sections : undefined;

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'swf-attendee-modal', {
          source: 'swf-attendee-modal',
          sections,
          itemCount: cart.item_count,
        })
      );

      this.close();
    } catch (err) {
      errorEl.textContent = err?.message || 'Something went wrong. Please try again.';
      errorEl.hidden = false;
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Add Ticket';
    }
  }

  #clearForm() {
    const form = this.refs.form;
    if (!form) return;
    for (const input of form.querySelectorAll('input')) {
      if (input.type !== 'hidden') input.value = '';
    }
    if (this.refs.error) this.refs.error.hidden = true;
  }
}

if (!customElements.get('swf-attendee-modal')) {
  customElements.define('swf-attendee-modal', SwfAttendeeModal);
}
